import type NextLink from 'next/link';

import { type QuantitativeSize } from '../../types/sizes';
import { establishUrlProtocol, type Path, type Url } from '../../util/urls';

export const MuiButtonVariants = ['contained', 'outlined', 'text'] as const;
/**
 * Represents the Material UI variants for the {@link Button} component.  These variants can be
 * plugged directly into the root Material UI {@link RootButton} component, and the {@link Button}
 * component will style itself based on the theme accordingly.
 */
export type MuiButtonVariant = (typeof MuiButtonVariants)[number];

export const InternalButtonVariants = ['link', 'inline-link'] as const;
/**
 * Represents internally defined variants for the {@link Button} component that are not associated
 * with Material UI.  These variants are still associated with a {@link MuiButtonVariant}, but are
 * used to apply custom styles to the {@link Button} component, overriding styles applied by the
 * associated {@link MuiButtonVariant}.
 *
 * Internal Variants
 * -----------------
 * 1. "link"
 *    For the "link" variant, the {@link Button} component will style itself as if the component
 *    only contains text, without any padding, borders or background color (for both steady and
 *    hover/focused states).  The height of the {@link Button} will be determined solely from the
 *    line-height of the text it contains.
 *
 * @example
 * The internal variant "link" is associated with the Material UI variant "text".  When the "link"
 * variant is applied, the component will style itself as if the "text" variant was applied, but
 * will apply overrides and additional styles on top.
 */
export type InternalButtonVariant = (typeof InternalButtonVariants)[number];

const isInternalButtonVariant = (variant: unknown): variant is InternalButtonVariant =>
  InternalButtonVariants.includes(variant as InternalButtonVariant);

export const isLinkOrInlineLinkVariant = (
  variant: unknown,
): variant is Extract<InternalButtonVariant, 'inline-link' | 'link'> =>
  typeof variant === 'string' && ['inline-link', 'link'].includes(variant);

export const ButtonVariants = [...InternalButtonVariants, ...MuiButtonVariants] as const;

/**
 * Defines the allowed variants for the {@link Button} component, which include both internally
 * defined variants, {@link InternalButtonVariant}, and Material UI variants,
 * {@link MuiButtonVariant}.
 */
export type ButtonVariant = (typeof ButtonVariants)[number];

const MuiButtonVariantMap: Record<InternalButtonVariant, MuiButtonVariant> = {
  'inline-link': 'text',
  link: 'text',
};

/**
 * Returns the Material UI variant for the {@link Button} component based on the provided variant,
 * which may include internally defined variants, {@link InternalButtonVariant}.
 */
export const getMuiButtonVariant = (variant: ButtonVariant): MuiButtonVariant =>
  isInternalButtonVariant(variant) ? MuiButtonVariantMap[variant] : variant;

export type LinkComponent = 'a' | typeof NextLink;

/**
 * Defines the form of the 'href' prop for the {@link Link} component based on the 'component' prop,
 * {@link E}.
 *
 * If the {@link Link} is being rendered as a {@link NextLink}, the 'href' prop should be an
 * internal pathname without a domain, loc or protocol, {@link Protocol}.  If the {@link Link} is
 * being rendered as an anchor element, the 'href' prop should be a full URL with a domain, loc and
 * protocol, {@link Protocol}.
 *
 * In the case that the {@link Link} component is being rendered as an anchor element, and the
 * protocol, {@link Protocol}, is not present on the 'href' prop, the protocol 'https' will be
 * assumed.
 */
export type LinkHref<E extends LinkComponent> = E extends 'a'
  ? Url
  : E extends typeof NextLink
    ? Path
    : never;

/**
 * Returns the appropriate 'href' prop for a {@link Link} or {@link IconLink} component based on the
 * 'component' prop, {@link E}, that the component is being rendered as.
 */
export const getLinkHref = <E extends LinkComponent>({
  component,
  href,
}: {
  component: E;
  href: LinkHref<E>;
}): Path | Url => {
  if (component === 'a') {
    return establishUrlProtocol(href as Url, 'https');
  }
  return href;
};

export const ButtonDiscreteSize = Object.freeze({
  Large: 'large',
  Medium: 'medium',
  Small: 'small',
  XSmall: 'xsmall',
} as const);

export type ButtonDiscreteSize = (typeof ButtonDiscreteSize)[keyof typeof ButtonDiscreteSize];

export const ButtonDefaultSize = ButtonDiscreteSize.Medium;

export type ButtonSize = 'auto' | ButtonDiscreteSize | QuantitativeSize<'px'>;

export const isButtonDiscreteSize = (size: unknown): size is ButtonDiscreteSize =>
  typeof size === 'string' &&
  Object.values(ButtonDiscreteSize).includes(size as ButtonDiscreteSize);

export const isMuiButtonSize = (
  size: unknown,
): size is Exclude<ButtonDiscreteSize, typeof ButtonDiscreteSize.XSmall> =>
  isButtonDiscreteSize(size) && size !== ButtonDiscreteSize.XSmall;

export const ButtonFontWeightName = Object.freeze({
  Bold: 'bold',
  Medium: 'medium',
  Regular: 'regular',
  Semibold: 'semibold',
} as const);

export type ButtonFontWeightName = (typeof ButtonFontWeightName)[keyof typeof ButtonFontWeightName];

export type ButtonFontWeightNumber = 400 | 500 | 600 | 700;

export const ButtonFontWeightMap: Record<ButtonFontWeightName, ButtonFontWeightNumber> = {
  [ButtonFontWeightName.Bold]: 700,
  [ButtonFontWeightName.Medium]: 500,
  [ButtonFontWeightName.Regular]: 400,
  [ButtonFontWeightName.Semibold]: 600,
};

const DefaultButtonFontWeights: Record<ButtonVariant, 'inherit' | ButtonFontWeightName> = {
  contained: ButtonFontWeightName.Semibold,
  'inline-link': 'inherit',
  link: ButtonFontWeightName.Regular,
  outlined: ButtonFontWeightName.Semibold,
  text: ButtonFontWeightName.Semibold,
};

export type ButtonFontWeight = 'inherit' | ButtonFontWeightName | ButtonFontWeightNumber;

export const parseButtonFontWeight = (
  fontWeight: ButtonFontWeight | undefined,
  variant: ButtonVariant,
): 'inherit' | ButtonFontWeightNumber => {
  if (fontWeight === undefined) {
    return parseButtonFontWeight(DefaultButtonFontWeights[variant], variant);
  } else if (typeof fontWeight === 'number' || fontWeight === 'inherit') {
    return fontWeight;
  }
  return ButtonFontWeightMap[fontWeight];
};
